import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/AppLayout";
import { ArrowRight, Printer, Copy, KeyRound, CreditCard } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, Tooltip, CartesianGrid } from "recharts";
import { PosterModal } from "../components/PosterModal";
import { KpiStrip } from "../components/dashboard/KpiStrip";

const card = "rounded-2xl bg-white border border-[#0B0D10]/10 p-5 sm:p-6";
const label = "text-[11px] uppercase tracking-[0.16em] text-[#0B0D10]/50 font-semibold";

function ActivityChart({ series }) {
  return (
    <div className={card} data-testid="activity-chart">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className={label}>Last 30 days</div>
          <h3 className="font-display font-bold text-[17px] tracking-[-0.01em] mt-1">Visits and rewards</h3>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-[12px] text-[#0B0D10]/60 pt-1">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#0B0D10]" />Visits</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#9ACC2E]" />Rewards</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={series} margin={{ left: -24, right: 4, top: 4 }}>
          <defs>
            <linearGradient id="bp-visits" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0B0D10" stopOpacity={0.14} /><stop offset="100%" stopColor="#0B0D10" stopOpacity={0} /></linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE1" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6b6e75" }} tickFormatter={(d) => d.slice(5)} interval={5} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E5DE", fontSize: 12, background: "#fff" }} />
          <Area type="monotone" dataKey="visits" stroke="#0B0D10" strokeWidth={2} fill="url(#bp-visits)" name="Visits" />
          <Area type="monotone" dataKey="redemptions" stroke="#9ACC2E" strokeWidth={2} fill="transparent" name="Rewards" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function PassCard({ org, program, onPoster }) {
  const live = program?.status === "live";
  const url = program ? `${window.location.origin}/api/j/${org.slug}/${program.slug}` : "";
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Install link copied"); } catch (_) { toast.error("Couldn't copy"); } };
  if (!program) {
    return (
      <div className="rounded-2xl bg-[#0B0D10] text-white p-6" data-testid="get-started-card">
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#C8FF4D] font-semibold">Your pass</div>
        <h3 className="font-display font-extrabold text-[22px] tracking-[-0.02em] mt-2 leading-tight">We're building it now.</h3>
        <p className="text-white/60 text-[13.5px] mt-2 leading-relaxed">Your pass is usually live within 48 hours of your call. You'll get an email with everything you need the moment it is.</p>
      </div>
    );
  }
  return (
    <div className={card} data-testid="pass-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className={label}>Your pass</div>
          <h3 className="font-display font-bold text-[17px] tracking-[-0.01em] mt-1">{program.name}</h3>
          <p className="text-[13px] text-[#0B0D10]/55 mt-1">{program.reward?.name ? `${program.reward.name} after ${program.reward.threshold || 10} ${program.type === "points" ? "points" : program.type === "stamp" ? "stamps" : "visits"}` : "Loyalty pass"}</p>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] ${live ? "bg-[#C8FF4D] text-[#0B0D10]" : "bg-[#F4F1E9] text-[#0B0D10]/60"}`} data-testid="pass-status">
          <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-[#0B0D10]" : "bg-[#0B0D10]/40"}`} />{live ? "Live" : program.status}
        </span>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-2">
        <button onClick={onPoster} disabled={!live} data-testid="tool-print-poster" className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full bg-[#0B0D10] text-white text-[13px] font-semibold hover:bg-black disabled:opacity-40 transition"><Printer size={14} />Print QR poster</button>
        <button onClick={copy} disabled={!live} data-testid="owner-test-pass-copy" className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full border border-[#0B0D10]/15 hover:border-[#0B0D10] text-[13px] font-semibold disabled:opacity-40 transition"><Copy size={14} />Copy install link</button>
      </div>
      <div className="mt-4 pt-4 border-t border-[#0B0D10]/[0.06] flex flex-wrap gap-x-5 gap-y-2 text-[12.5px]">
        <Link to="/app/scanner" data-testid="pass-card-scanning" className="inline-flex items-center gap-1.5 text-[#0B0D10]/70 hover:text-[#0B0D10] font-medium"><KeyRound size={13} />Staff PIN &amp; scanning</Link>
        <Link to="/app/programs" data-testid="pass-card-programs" className="inline-flex items-center gap-1.5 text-[#0B0D10]/70 hover:text-[#0B0D10] font-medium"><CreditCard size={13} />Pass details</Link>
      </div>
    </div>
  );
}

function RecentActivity({ acts }) {
  return (
    <div className={card} data-testid="recent-activity">
      <div className={label}>Recent activity</div>
      {acts.length === 0 ? <p className="text-[13px] text-[#0B0D10]/50 mt-3">No activity yet.</p> : (
        <ul className="divide-y divide-[#0B0D10]/[0.06] mt-2">
          {acts.slice(0, 8).map((ev) => (
            <li key={ev.id} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <div className="text-[13px] font-medium truncate">{ev.title}</div>
                <div className="text-[12px] text-[#0B0D10]/50 truncate">{ev.detail}</div>
              </div>
              <span className="text-[11px] text-[#0B0D10]/40 shrink-0 tabular-nums">{new Date(ev.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { activeOrg } = useAuth();
  const [a, setA] = useState(null);
  const [acts, setActs] = useState([]);
  const [progs, setProgs] = useState([]);
  const [posterFor, setPosterFor] = useState(null);
  useEffect(() => {
    if (!activeOrg) return;
    api.get(`/orgs/${activeOrg.id}/analytics`).then((r) => setA(r.data)).catch(() => {});
    api.get(`/orgs/${activeOrg.id}/activity`).then((r) => setActs(r.data)).catch(() => {});
    api.get(`/orgs/${activeOrg.id}/programs`).then((r) => setProgs(r.data)).catch(() => {});
  }, [activeOrg]);
  if (!activeOrg) return null;
  const primary = progs.find((p) => p.status === "live") || progs[0];

  return (
    <div className="space-y-5">
      <PageHeader title="Overview" subtitle={activeOrg.name}
        action={<Link to="/app/scanner" data-testid="dash-open-scanner" className="bg-[#C8FF4D] text-[#0B0D10] rounded-full h-10 px-4 font-semibold text-[13px] inline-flex items-center gap-1.5 hover:bg-[#b8f538] transition">Scanning <ArrowRight size={14} /></Link>} />
      {a && <KpiStrip a={a} />}
      {a && <ActivityChart series={a.series} />}
      <div className="grid lg:grid-cols-2 gap-5 items-start">
        <PassCard org={activeOrg} program={primary} onPoster={() => setPosterFor(primary)} />
        <RecentActivity acts={acts} />
      </div>
      <PosterModal open={!!posterFor} program={posterFor} orgName={activeOrg.name} orgSlug={activeOrg.slug} onClose={() => setPosterFor(null)} />
    </div>
  );
}
